import { z } from 'zod';

/**
 * Accounting-connection shared contract (Qoyod accounting sync, slice 1).
 *
 * Single source of truth for the per-company connection wire shape shared by
 * the web SPA (`src/services/accounting.ts`) and the NestJS accounting slice
 * (`apps/api/src/accounting/*`). The API key is WRITE-ONLY — the view never
 * echoes it, only `hasApiKey`. `.passthrough()` per conventions §8 so the SPA
 * stays tolerant of new server fields.
 */

/** Fee-type (InvoiceKind) -> Qoyod GL account CODE. */
export const feeAccountSchema = z
  .object({
    feeType: z.string(),
    accountCode: z.string(),
  })
  .passthrough();
export type FeeAccount = z.infer<typeof feeAccountSchema>;

/** Redacted connection view (never carries the API key). */
export const accountingConnectionViewSchema = z
  .object({
    companyId: z.string(),
    provider: z.literal('qoyod'),
    status: z.enum(['active', 'disabled']),
    hasApiKey: z.boolean(),
    baseUrl: z.string(),
    inventoryId: z.number(),
    taxId: z.number(),
    productUnitTypeId: z.number(),
    categoryId: z.number(),
    feeAccounts: z.array(feeAccountSchema),
    updatedAt: z.string(),
  })
  .passthrough();
export type AccountingConnectionView = z.infer<
  typeof accountingConnectionViewSchema
>;

/** Upsert payload — `apiKey` is write-only + optional (omit to keep the stored key). */
export const upsertConnectionInputSchema = z
  .object({
    apiKey: z.string().optional(),
    status: z.enum(['active', 'disabled']),
    baseUrl: z.string().optional(),
    inventoryId: z.number(),
    taxId: z.number(),
    productUnitTypeId: z.number(),
    categoryId: z.number(),
    feeAccounts: z.array(feeAccountSchema),
  })
  .passthrough();
export type UpsertConnectionInput = z.infer<typeof upsertConnectionInputSchema>;

/**
 * The company-collected fee kinds pushed to the accounting provider — the
 * single source for the API scope guard (`isSyncableFeeKind`) AND the
 * super-admin fee-account-map UI. Array order is the UI presentation order
 * (brokerage first — the CEO's 410101 anchor); the API consumes it as a Set.
 * Adding a kind here fails FE compile until its bilingual label is added.
 */
export const SYNCABLE_FEE_KINDS = [
  'brokerage_fee',
  'marketing_fee',
  'property_management_fee',
  'contract_fee',
  'management_fee',
  'ejar_fee',
  'subscription_fee',
  'monthly_maintenance',
] as const;
export type SyncableFeeKind = (typeof SYNCABLE_FEE_KINDS)[number];

/**
 * Per-invoice Qoyod sync state for the finance UI.
 *
 * Served by `GET /invoices/accounting-sync-state` — on the INVOICES controller
 * under `finance.view`, not the super-admin accounting routes, because the
 * audience is company finance staff.
 *
 * Only invoices that entered the sync pipeline appear. An invoice absent from
 * the list has no badge: correct for rent/sale (the scope guard excludes them)
 * and self-correcting for a fee invoice the reconciliation sweep has yet to
 * pick up.
 */
export const invoiceSyncStateSchema = z
  .object({
    invoiceId: z.string(),
    /**
     * `failed` means the job is DEAD (attempts exhausted). A job still retrying
     * reports `queued` — showing "failed" mid-retry sends an operator chasing
     * something about to succeed on its own.
     *
     * `blocked` is NOT a failure: the org's accounting configuration cannot
     * satisfy the push yet (a fee kind with no GL account code, or a code absent
     * from the provider's chart of accounts — `accounting-008`). The job parks
     * WITHOUT burning attempts and drains itself once the configuration is
     * completed, so it must read as "waiting on setup", never as "broken".
     */
    state: z.enum(['synced', 'queued', 'blocked', 'failed']),
    providerInvoiceId: z.string().nullable(),
    /** The unique reference we sent Qoyod — what to search for in their dashboard. */
    reference: z.string().nullable(),
    syncedAt: z.string().nullable(),
    /** Coded `accounting-00X` string on a dead or blocked job. */
    lastError: z.string().nullable(),
    /**
     * The Dallali fee kind this invoice classified as. Drives the ledger line on
     * the badge, and is what makes an unmapped kind visible BEFORE it blocks.
     * A bare string, not `SyncableFeeKind`: the server classifies, and the UI
     * must render a kind it has no label for rather than crash.
     */
    feeKind: z.string().nullable(),
    /** Qoyod GL account code the fee books to (e.g. "410101"); null when unmapped. */
    accountCode: z.string().nullable(),
    /**
     * Provider-side lifecycle, in HOUSE labels — the API normalises Qoyod's wire
     * strings (it sends "Partially Paid" with a space) at the adapter boundary.
     *
     * Ours says we sent it; this says whether the accountant has approved it,
     * which is the half that closes their weekly reconciliation pass. Null means
     * never read back — deliberately distinct from a verified `draft`.
     */
    providerStatus: z
      .enum(['draft', 'approved', 'partially_paid', 'paid'])
      .nullable(),
  })
  .passthrough();
export type InvoiceSyncState = z.infer<typeof invoiceSyncStateSchema>;
